import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import isEmpty from 'lodash/isEmpty';
import en from 'config/locales/en';
import * as formTypes from 'constants/formTypes';
import documentTypes from 'constants/enums/foreignBornDocumentTypes';
import documentTypesSF85P from 'constants/enums/foreignBornSF85PDocumentTypes';
import { isFormSF852013, isPVQForm } from 'helpers/form';

const { yes, no, dnk } = en.branch.label;
const { dnk: idk } = en.branch.value;

// Prefilters used to filter out data not acceptable by the UI (usually created by old prefill data)
export const prefilterBranch = (data) => {
  let newData = data;
  const path = 'props.value';
  const value = get(data, path);
  if (value && ![yes, no, dnk, idk].includes(value)) {
    newData = cloneDeep(data);
    set(newData, path, '');
  }
  return newData;
};

export const prefilterAlcoholOrdered = (data, formInfo = { form_type: '', form_version: '' }) => {
  const itemsPath = 'props.List.props.items';
  const items = get(data, itemsPath);
  const seekersPath = 'Item.Seekers.props.values';
  const containsSeekersField = items.filter((item) => get(item, seekersPath, [])?.length > 0)?.length > 0;

  if (formInfo.form_type === formTypes.SF86 && containsSeekersField) {
    const acceptedSeekerValues = ['Employer', 'MedicalProfessional', 'MentalHealthProfessional', 'CourtOfficial', 'NotOrdered', 'Other'];
    const newData = cloneDeep(data);
    const newItems = items.map((item) => {
      const seekers = get(item, seekersPath, []);
      if (seekers?.length > 0) {
        const filteredSeekers = seekers.filter((seeker) => acceptedSeekerValues.includes(seeker));
        set(item, seekersPath, filteredSeekers);
      }
      return item;
    });
    set(newData, itemsPath, newItems);
    return newData;
  }

  return data;
};

export const prefilterForeignBornDocument = (data, formInfo = { form_type: '', form_version: '' }) => {
  if ([formTypes.PVQ_A_B, formTypes.PVQ_A_B_C, formTypes.PVQ_A_B_D].includes(formInfo.form_type)) {
    return data;
  }

  const acceptableDocumentTypes = [formTypes.SF85P, formTypes.SF85PS].includes(formInfo.form_type) ? documentTypesSF85P : documentTypes;
  let newData = data;
  const path = 'props.DocumentType.props.value';
  const value = get(data, path);
  if (value && !acceptableDocumentTypes.includes(value)) {
    newData = cloneDeep(data);
    set(newData, path, '');
  }
  return newData;
};

export const prefilterCitizenshipStatusDocument = (data, formInfo = { form_type: '', form_version: '' }) => {
  const newData = cloneDeep(data);

  if (!isPVQForm(formInfo.form_type)) {
    // This is only valid for SF Forms.
    const alienRegistrationNumberNotApplicablePath = 'props.AlienRegistrationNumberNotApplicable.props.applicable';
    const alienRegistrationNumberNotApplicable = get(data, alienRegistrationNumberNotApplicablePath);
    const alienRegistrationNumberExplanation = get(data, 'props.AlienRegistrationNumberExplanation.props.value');
    if (alienRegistrationNumberNotApplicable !== true && isEmpty(alienRegistrationNumberExplanation)) {
      set(newData, alienRegistrationNumberNotApplicablePath, true);
    }
  }

  if (!isPVQForm(formInfo.form_type)) {
    const entryDateNotApplicablePath = 'props.EntryDateNotApplicable.props.applicable';
    const entryDateNotApplicable = get(data, entryDateNotApplicablePath);
    const entryDateExplanation = get(data, 'props.EntryDateExplanation.props.value');
    if (entryDateNotApplicable !== true && isEmpty(entryDateExplanation)) {
      set(newData, entryDateNotApplicablePath, true);
    }
  }

  const entryLocationNotApplicablePath = 'props.EntryLocationNotApplicable.props.applicable';
  const entryLocationNotApplicable = get(data, entryLocationNotApplicablePath);
  const entryLocationExplanation = get(data, 'props.EntryLocationExplanation.props.value');
  if (entryLocationNotApplicable !== true && isEmpty(entryLocationExplanation)) {
    set(newData, entryLocationNotApplicablePath, true);
  }
  return newData;
};

export const prefilterMilitaryHistory = (data, formInfo = { form_type: '', form_version: '' }) => {
  let newData = data;
  const path = 'props.HasServedMerchantMarine.props.value';
  const value = get(data, path);

  // Clear Merchant Marine data if exists in non SF852013 form
  if (!isFormSF852013(formInfo.form_type, formInfo.form_version) && value) {
    newData = cloneDeep(data);
    set(newData, path, '');
  }
  return newData;
};

export const prefilterMilitarySelective = (data, formInfo = { form_type: '', form_version: '' }) => {
  let newData = data;
  const path = 'props.HasRegistered.props.value';
  const value = get(data, path);

  // Clear HasRegistered data if not SF852013 form and incorrect value
  if (!isFormSF852013(formInfo.form_type, formInfo.form_version) && !['', 'Yes', 'No'].includes(value)) {
    newData = cloneDeep(data);
    set(newData, path, '');
  }
  return newData;
};
